import { useContext, useState } from 'react';
import { useQueryClient } from 'react-query';
import { apigeeEndpoint } from '../apis';
import { LoadingContext } from '../contexts/LoadingContextProvider';
import { ListRetrievesRequest, PaginatedRetrieveTableView } from '@tff/types/tff/retrieve-flight-service';

export type ListRetrievesParams = NonNullable<ListRetrievesRequest['params']>;

export type ListRetrievesHookResponse = {
  data: PaginatedRetrieveTableView;
  status: 'IDLE' | 'LOADING' | 'SUCCESS' | 'FAILURE';
  error?: unknown;
  fetchRetrieves: (params: ListRetrievesParams) => void;
  clearCache: () => void;
};

export const useListRetrieves = (): ListRetrievesHookResponse => {
  const [data, setData] = useState<PaginatedRetrieveTableView>({ items: [], total: 0 });
  const [status, setStatus] = useState<ListRetrievesHookResponse['status']>('IDLE');
  const [error, setError] = useState<unknown>();
  const { showLoading, closeLoading } = useContext(LoadingContext);
  const queryClient = useQueryClient();

  const clearCache = () => {
    queryClient.removeQueries({ predicate: query => (query.queryKey as string).startsWith('listRetrieves') });
  };

  const fetchRetrieves = async (params: ListRetrievesParams) => {
    const queryKey = `listRetrieves:${params.status}:${params.limit}:${params.cursor ?? ''}`;

    try {
      showLoading('Fetching retrieves...');
      setStatus('LOADING');

      const response = await queryClient.fetchQuery<PaginatedRetrieveTableView>(
        queryKey,
        async () => {
          const queryParams = new URLSearchParams();
          if (params.status) queryParams.append('status', params.status);
          if (params.limit) queryParams.append('limit', String(params.limit));
          if (params.cursor) queryParams.append('cursor', params.cursor);

          const query = queryParams.toString();
          const url = query ? `/retrieves?${query}` : '/retrieves';

          return apigeeEndpoint.get<PaginatedRetrieveTableView>(url).then(res => res.data);
        },
        {
          cacheTime: 5 * 60 * 1000,
          staleTime: 5 * 60 * 1000,
          initialData: () => queryClient.getQueryData<PaginatedRetrieveTableView>(queryKey),
        },
      );

      setData(response);
      setStatus('SUCCESS');
    } catch (err) {
      setError(err);
      setStatus('FAILURE');
    } finally {
      closeLoading();
    }
  };

  return { data, status, error, fetchRetrieves, clearCache };
};
